import { loadMotion, SPRING } from '@theme/motion';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Subtle press feedback: a gentle scale-down on press for primary buttons and
 * product-card media, springing back on release.
 *
 * Delegated from the document so it covers dynamically-added elements (infinite
 * scroll, quick-add, etc.) without re-binding. Progressive enhancement — it does
 * nothing when the user prefers reduced motion, and Motion is loaded lazily on
 * the first press (so the very first press on a cold cache may not animate;
 * every press after that is instant).
 */

const PRESS_SCALE = 0.97;
const BUTTON_SELECTOR = '.button, .button-secondary';
const CARD_SELECTOR = '.product-card';

/** @type {Set<Element>} Elements currently held down. */
const pressedElements = new Set();

/**
 * Resolves the element to animate for an event target: the button itself, or a
 * product card's media wrapper (`.card-gallery`) — never the card root, which
 * already owns a hover transform we'd fight.
 *
 * @param {EventTarget | null} node
 * @returns {Element | null}
 */
function resolvePressTarget(node) {
	if (!(node instanceof Element)) return null;

	const button = node.closest(BUTTON_SELECTOR);
	if (button) return button.matches('[disabled], [aria-disabled="true"]') ? null : button;

	const card = node.closest(CARD_SELECTOR);
	if (card) return card.querySelector('.card-gallery');

	return null;
}

/**
 * Animates `target` toward its current pressed/released scale. The pressed state
 * is re-read once Motion resolves, so a quick press→release settles correctly.
 *
 * @param {Element} target
 * @param {boolean} isPressed
 */
function setPressed(target, isPressed) {
	if (prefersReducedMotion()) return;

	if (isPressed) pressedElements.add(target);
	else pressedElements.delete(target);

	loadMotion().then(({ animate }) => {
		animate(target, { scale: pressedElements.has(target) ? PRESS_SCALE : 1 }, SPRING.subtle);
	});
}

/** Releases every currently-pressed element. */
function releaseAll() {
	for (const target of [...pressedElements]) setPressed(target, false);
}

document.addEventListener(
	'pointerdown',
	(event) => {
		// Primary mouse button, or any touch/pen press.
		if (event.pointerType === 'mouse' && event.button !== 0) return;

		const target = resolvePressTarget(event.target);
		if (target) setPressed(target, true);
	},
	{ capture: true }
);

document.addEventListener('pointerup', releaseAll, { capture: true });
document.addEventListener('pointercancel', releaseAll, { capture: true });
